<template>
    <!-- AI 页面主体：根据页面状态切换首页或聊天界面 -->
    <main class="ai-main" :class="`${vm.initReady?'ready':''} is-${vm.view}`">
        <div ref="conversationScrollRef" class="ai-main-content">
            <button v-if="vm.features.showBack" class="ai-welcome-back" type="button" @click="goToHome">
                {{ t('ai.home.back') }}
            </button>
            <!-- 首页欢迎区：显示标题、搜索模式和推荐标签 -->
            <div v-if="vm.view === 'home'" class="ai-home-shell">
                <AiWelcome :t="t" :mode="vm.searchMode"
                    :title="runtimeConfig.page_title || ''" :description="runtimeConfig.page_desc || ''"
                    :show-description="vm.features.showDescription" :show-mode-switch="vm.features.showModeSwitch"
                    @update:mode="vm.searchMode = $event"
                    @regular-search="handleRegularSearch" />
            </div>

            <!-- 对话区：首页之外显示聊天记录和产品图片 -->
            <ConversationView v-else :t="t" :title="runtimeConfig.page_title || ''" :messages="vm.messages"
                @home="goToHome" @new-chat="goToNewChat" @close="goToNewChat" @regular-search="openRegularSearch"
                @suggest-question="useSuggestedQuestion" />

            <!-- 输入区：处理文字、技能选择和文件上传 -->
            <div class="ai-composer-shell-shadow">
                <div class="shadow"></div>
                <div class="ai-composer-shell">
                    <PromptComposer v-model="vm.draft" :t="t"
                        :placeholder="vm.placeholder"
                        :uploads="vm.uploads" :skills="vm.skills" :selected-skill="vm.selectedSkill" :disabled="vm.submitting"
                        :upload-disabled="vm.searchMode === 'regular'" :skill-disabled="vm.searchMode === 'regular'"
                        @select-files="addUploads" @remove-upload="removeUpload" @select-skill="vm.selectedSkill = $event"
                        @submit="submitPrompt" />
                </div>
            </div>

            <AiSuggestions v-if="vm.view === 'home'" :suggestions="vm.suggestions" :selected-tags="vm.selectedTags"
                @toggle-tag="toggleTag" />
        </div>

        <!-- 页面底部链接 -->
        <AiFooterLinks />
    </main>
</template>

<script setup lang="ts">
// 导入 Vue 响应式和生命周期工具
import { nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';

// 导入 AI 页面使用的子组件
import AiFooterLinks from '@/components/ai/AiFooterLinks.vue';
import AiSuggestions from '@/components/ai/AiSuggestions.vue';
import AiWelcome from '@/components/ai/AiWelcome.vue';
import ConversationView from '@/components/ai/ConversationView.vue';
import PromptComposer from '@/components/ai/PromptComposer.vue';

// 导入页面数据的 TypeScript 类型
import type {
    AiMessage,
    AiView,
    SearchMode,
    SkillOption,
    UploadItem,
} from '@/components/ai/types';

// 导入 WordPress 运行时配置
import { runtimeConfig } from '@/config/runtime';
import { ApiError } from '@/tools/api';
import {
    getChatSiteByLanguage,
    imageFileToDataUrl,
    sendChatMessage,
} from '@/tools/chat_api';
import type { ChatRequest } from '@/tools/chat_api';
import { useLang } from '@/tools/lang';
import { MachineOutput } from '@/tools/machine_output';
import { getTagHits } from '@/tools/tag_hits_api';
import type { SuggestionOption } from '@/tools/tag_hits_api';
import { initPageWithAnimation, syncWordPressHeader } from '@/tools/tools';

// 创建多语言工具，取得当前语言、翻译函数和语言包加载方法
const { currentLang, t, loadLanguage } = useLang();

interface ApiResponseMessage {
    code?: unknown;
    message?: unknown;
}

interface ConversationHistoryItem {
    conversation_id: string;
    first_question: string;
}

const CONVERSATION_HISTORY_STORAGE_KEY = 'sunon_ai_conversation_history';

// 首轮请求成功后保存会话编号和首个问题；同一个会话只记录一次。
const saveConversationHistory = (conversationId: string, firstQuestion: string) => {
    if (!conversationId) return;

    try {
        const storedValue = window.localStorage.getItem(CONVERSATION_HISTORY_STORAGE_KEY);
        const parsedValue: unknown = storedValue ? JSON.parse(storedValue) : [];
        const history = Array.isArray(parsedValue)
            ? parsedValue as ConversationHistoryItem[]
            : [];

        if (history.some(item => item?.conversation_id === conversationId)) return;

        history.push({
            conversation_id: conversationId,
            first_question: firstQuestion,
        });
        window.localStorage.setItem(CONVERSATION_HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
        // localStorage 可能因隐私模式、容量限制或旧数据格式损坏而不可用。
        console.error('[AI Search] Failed to save conversation history', error);
    }
};

// WordPress 自身错误与第三方错误仍保留结构化信息，供重试判断和控制台排查。
const getApiResponseMessage = (error: ApiError): { code: string; message: string } => {
    const response = typeof error.data === 'object' && error.data !== null
        ? error.data as ApiResponseMessage
        : undefined;

    return {
        code: typeof response?.code === 'string' ? response.code : '',
        message: typeof response?.message === 'string' ? response.message : error.message,
    };
};

const showRequestError = (error: unknown) => {
    if (!(error instanceof ApiError)) {
        console.log('[AI Search] 请求失败', error);
        return;
    }

    const { code, message } = getApiResponseMessage(error);
    // 当前产品策略不向用户展示接口错误，避免非关键接口故障打断对话体验。
    console.log('[AI Search] 请求失败', {
        status: error.status,
        code,
        message,
        response: error.data,
    });
};

// 聊天内容的实际滚动容器，用于在一轮回答和卡片全部渲染后定位到底部。
const conversationScrollRef = ref<HTMLElement | null>(null);

const scrollConversationToBottom = async () => {
    await nextTick();
    const container = conversationScrollRef.value;
    if (!container || vm.view !== 'chat') return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
};

/**
 * 接口仍返回完整 JSON；当前实例负责把其中的 answer 模拟成分批文本输出。
 * 不放入 reactive，避免 Vue 代理类实例中的计时器和内部状态。
 */
let activeOutput: MachineOutput<AssistantStructure> | null = null;

type AssistantStructure = Pick<AiMessage,
  | 'products'
  | 'showrooms'
  | 'suggestedQuestions'
  | 'intent'
  | 'explanation'
  | 'meta'
  | 'kbSourceIds'
  | 'conversationId'
  | 'requestId'
  | 'runId'
  | 'traceId'
>;

// 页面全部响应式状态及其类型定义
const vm = reactive<{
    view: AiView;
    searchMode: SearchMode;
    draft: string;
    uploads: UploadItem[];
    submitting: boolean;
    placeholder: string;
    selectedSkill: string;
    selectedTags: string[];
    skills: SkillOption[];
    suggestions: SuggestionOption[];
    messages: AiMessage[];
    conversationId: string;
    activeRequest: AbortController | null;
    initReady: boolean;
    features: {
        showBack: boolean;
        showDescription: boolean;
        showModeSwitch: boolean;
    };
}>({
    initReady: false,
    view: 'home',
    searchMode: 'ai',
    draft: '',
    placeholder: runtimeConfig.placeholder,
    uploads: [],
    submitting: false,
    selectedSkill: 'all',
    selectedTags: [],

    // 输入框中可选择的 AI 技能
    skills: [
        { key: 'all', titleKey: 'ai.skills.all.title', descriptionKey: 'ai.skills.all.description' },
        { key: 'product', titleKey: 'ai.skills.product.title', descriptionKey: 'ai.skills.product.description' },
        // { key: 'recommendation', titleKey: 'ai.skills.recommendation.title', descriptionKey: 'ai.skills.recommendation.description' },
        // { key: 'case', titleKey: 'ai.skills.case.title', descriptionKey: 'ai.skills.case.description' },
    ],

    // 首页展示的推荐搜索标签
    suggestions: [],
    messages: [],
    conversationId: '',
    activeRequest: null,

    // 根据语言控制部分界面的显示状态
    features: {
        showBack: currentLang === 'en',
        showDescription: currentLang === 'en',
        showModeSwitch: currentLang === 'en',
    },
});

// 首页使用 WordPress 公共 Header；进入对话后改用 AI 独立 Header。
// Vite 开发环境不存在 WordPress Header，工具函数会直接跳过。
watch(
    () => vm.view,
    view => syncWordPressHeader(view === 'home'),
    { flush: 'post' },
);

// 推荐词可多选并追加到输入框；已选状态不跟随用户手动编辑反向恢复。
const toggleTag = (value: string) => {
    if (vm.selectedTags.includes(value)) return;

    vm.selectedTags.push(value);
    vm.draft += (vm.draft ? ' ' : '') + value;
};

// 用户编辑输入框并移除某个推荐词后，恢复该标签的可选状态。
watch(
    () => vm.draft,
    draft => {
        const retainedTags = vm.selectedTags.filter(tag => draft.includes(tag));
        if (retainedTags.length === vm.selectedTags.length) return;
        vm.selectedTags.splice(0, vm.selectedTags.length, ...retainedTags);
    },
);

// 添加用户选择的上传文件，并生成本地预览地址
const addUploads = (files: File[]) => {
    files.forEach((file) => {
        vm.uploads.push({
            id: `${Date.now()}-${Math.random()}`,
            file,
            url: URL.createObjectURL(file),
        });
    });
};

// 删除上传文件并释放对应的本地预览地址
const removeUpload = (id: string) => {
    const index = vm.uploads.findIndex(item => item.id === id);
    if (index === -1) return;
    URL.revokeObjectURL(vm.uploads[index].url);
    vm.uploads.splice(index, 1);
};

// 以当前站点根地址拼接 WordPress 搜索参数，并在新标签页打开。
const openRegularSearch = (query: string) => {
    const url = new URL('/', window.location.origin);
    url.searchParams.set('s', query.trim());
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
};

// 切换到普通搜索。相关上传和技能状态由 searchMode watcher 统一重置。
const handleRegularSearch = () => {
    const headerSearchBtn = document.querySelector('.header .wp .hd-r .so .so-btn') as HTMLDivElement;
    if(headerSearchBtn){
        headerSearchBtn.click();
    }else{
        vm.searchMode = 'regular';
    }
};

// 释放一组上传项的 Object URL，避免切换对话后仍占用浏览器内存。
const releaseUploads = (uploads: UploadItem[]) => {
    uploads.forEach(item => URL.revokeObjectURL(item.url));
};

// 提交问题并用同一条助手占位消息承接加载状态和最终接口结果。
const submitPrompt = async () => {
    const question = vm.draft.trim();
    if ((!question && !vm.uploads.length) || vm.submitting) return;

    if (vm.searchMode === 'regular') {
        openRegularSearch(question);
        return;
    }

    const submittedUploads = [...vm.uploads];
    const assistantId = `assistant-${Date.now()}-${Math.random()}`;
    const controller = new AbortController();
    const thinkingStartedAt = performance.now();
    const isFirstQuestion = !vm.conversationId;
    const submittedQuestion = question || t('ai.chat.question');

    vm.messages.push({ id: `user-${Date.now()}`, role: 'user', content: submittedQuestion });
    vm.messages.push({
        id: assistantId,
        role: 'assistant',
        content: '',
        thinkingKey: 'ai.chat.thinking',
        loading: true,
    });
    vm.view = 'chat';
    vm.submitting = true;
    vm.activeRequest = controller;

    try {
        const payload: ChatRequest = {
            text: question || undefined,
            conversation_id: vm.conversationId || undefined,
            limit: 10,
            lang: runtimeConfig.lang,
        };

        const skillIntents = {
            product: 'product_search',
            recommendation: 'open_qa',
            case: 'open_qa',
        } as const;
        const selectedIntent = skillIntents[vm.selectedSkill as keyof typeof skillIntents];
        if (selectedIntent) {
            payload.intent = selectedIntent;
        }

        if (submittedUploads.length) {
            /**
             * 页面有意保留多图选择和预览能力，方便上游未来扩展为图片数组。
             * 当前 AI Search 协议只有一个 `image` 字段，因此本次请求只能转换并上传
             * 用户排序中的第一张图；不要把文件选择器改成单选，也不要在这里循环请求。
             */
            payload.image = await imageFileToDataUrl(submittedUploads[0].file);
            payload.intent = 'image_search';
        }
        payload.site = getChatSiteByLanguage();

        
        vm.draft = '';
        // All Skills 首次不传 intent/site。若上游识别为产品搜索并要求站点，再补 site 重试一次。
        let response;
        try {
            response = await sendChatMessage(payload, controller.signal);
        } catch (error) {
            const responseMessage = error instanceof ApiError
                ? getApiResponseMessage(error)
                : { code: '', message: '' };
            const requiresSite = responseMessage.code === 'error'
                && /site is required for product_search and image_search/i.test(responseMessage.message);
            if (payload.intent || !requiresSite) throw error;

            response = await sendChatMessage({
                ...payload,
                site: getChatSiteByLanguage(),
            }, controller.signal);
        }
        // 第三方业务错误可能使用 HTTP 200，因此还需要检查统一响应外壳。
        if (response.code === 'error') {
            throw new ApiError(response.message, 200, response);
        }
        const data = response.data;
        if (isFirstQuestion) {
            saveConversationHistory(data.conversation_id, submittedQuestion);
        }
        const assistantMessage = vm.messages.find(message => message.id === assistantId);
        if (!assistantMessage) return;

        // 先写入诊断和输出状态；产品、展厅等结构数据会在正文结束后统一挂载。
        Object.assign(assistantMessage, {
            content: '',
            loading: false,
            streaming: true,
            thinkingDurationMs: performance.now() - thinkingStartedAt,
        });

        const structure: AssistantStructure = {
            products: data.products,
            showrooms: data.showrooms,
            suggestedQuestions: data.suggested_questions,
            intent: data.intent,
            explanation: data.explanation,
            meta: data.meta,
            kbSourceIds: data.kb_source_ids,
            conversationId: data.conversation_id,
            requestId: data.request_id,
            runId: data.run_id,
            traceId: data.trace_id,
        };

        vm.conversationId = data.conversation_id;
        releaseUploads(submittedUploads);
        vm.uploads.splice(0);

        const output = new MachineOutput({
            text: data.answer,
            structure,
            onText: (content) => {
                const message = vm.messages.find(item => item.id === assistantId);
                if (message) message.content = content;
            },
            onStructure: (result) => {
                const message = vm.messages.find(item => item.id === assistantId);
                if (message) Object.assign(message, result);
            },
            onComplete: () => {
                const message = vm.messages.find(item => item.id === assistantId);
                if (message) message.streaming = false;
                // 等正文、卡片和推荐问题完成 DOM 更新后，再滚动到本轮内容底部。
                void scrollConversationToBottom();
            },
        });
        activeOutput = output;
        await output.start();
        if (activeOutput === output) activeOutput = null;
    } catch (error) {
        const index = vm.messages.findIndex(message => message.id === assistantId);
        if (index !== -1) vm.messages.splice(index, 1);

        // 重置对话和组件卸载产生的主动取消不应提示为请求故障。
        if (!controller.signal.aborted) {
            showRequestError(error);
        }
    } finally {
        if (vm.activeRequest === controller) vm.activeRequest = null;
        vm.submitting = false;
    }
};

// 推荐问题只写入输入框，由用户确认或修改后再发送，避免误触消耗请求额度。
const useSuggestedQuestion = (question: string) => {
    vm.draft = question;
};

const goToHome = () => {
    window.location.assign(new URL('/', window.location.origin));
};

const goToNewChat = () => {
    window.location.href = '';
};

// 普通搜索不支持图片和技能，切换时立即恢复默认状态并释放图片预览。
watch(
    () => vm.searchMode,
    (mode) => {
        if (mode !== 'regular') return;
        vm.selectedSkill = 'all';
        releaseUploads(vm.uploads);
        vm.uploads.splice(0);
    },
);

// 清空聊天内容并返回首页
const resetChat = () => {
    activeOutput?.cancel();
    activeOutput = null;
    vm.activeRequest?.abort();
    vm.activeRequest = null;
    vm.view = 'home';
    vm.messages.splice(0);
    vm.draft = '';
    vm.selectedTags.splice(0);
    vm.conversationId = '';
    releaseUploads(vm.uploads);
    vm.uploads.splice(0);
};

// 定义页面需要完成的异步初始化动作
const init = async () => {
    const initLang = async() => {
        try {
            // 加载语言 JSON，确保首次显示时不会出现翻译 key
            await loadLanguage(currentLang);
        } catch (error) {
            console.error('[AI Page]', error);

            if (currentLang !== 'ch') {
                try {
                    await loadLanguage('ch');
                } catch (fallbackError) {
                    console.error('[AI Page] 默认语言加载失败', fallbackError);
                }
            }
        }
    }
    const initSuggestions = async () => {
        try {
            const response = await getTagHits();
            vm.suggestions = response.data.queried;
        } catch (error) {
            console.error('[AI Page] 热门标签加载失败', error);
        }
    };
    await initPageWithAnimation(async () => {
        await Promise.all([initLang(), initSuggestions()]);
    });
    vm.initReady = true;
    console.log('加载完成');
};

// 将初始化动作交给等待效果统一执行，结束后显示页面
onMounted(async () => {
    await init();
    syncWordPressHeader(vm.view === 'home');
});

// 页面销毁前释放所有上传文件的本地预览地址
onBeforeUnmount(() => {
    activeOutput?.cancel();
    activeOutput = null;
    vm.activeRequest?.abort();
    releaseUploads(vm.uploads);
    syncWordPressHeader(true);
});
</script>

<style scoped lang="scss">
.ai-main {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    margin: 0;
    color: var(--text);
    background: #fff;
    overflow-x: hidden;
    overflow-y: auto;
    opacity: 0;
    transition: opacity .3s ease;

    &.ready { opacity: 1; }

    :deep(button),
    :deep(input),
    :deep(textarea) { font: inherit; }

    :deep(button),
    :deep(a) { -webkit-tap-highlight-color: transparent; }

    &.is-home {
        min-height: 100%;
        background: var(--ai-background-image, url('@/assets/images/ai/bg1.jpg')) center / cover no-repeat;

        .ai-main-content {
            flex: 1 0 auto;
            justify-content: center;
        }

        .ai-home-shell { flex: 0 0 auto; }

        .ai-composer-shell {
            width: min(780px, 100%);
            margin: 26px auto 0;
        }

        :deep(.ai-footer-links) {
            order: 3;
            margin-top: auto;
            padding-bottom: 14px;
        }
    }

    &.is-chat {
        overflow-y: auto;

        .ai-main-content {
            height: 100%;
            overflow-y: auto;
        }

        .ai-composer-shell-shadow{
            position: sticky;
            bottom: 0;
            z-index: 10;
            background-color: #fff;
            padding-bottom: 40px;
            .shadow{
                height: 60px;
                width: 100%;
                background: linear-gradient(0deg, #fff, transparent);
                position: absolute;
                top: 0;
                transform: translateY(-100%);
            }
        }
        .ai-composer-shell {
            flex: 0 0 auto;
            width: min(780px, calc(100% - 40px));
            margin: 14px auto 0;
        }

        :deep(.prompt-composer) {
            min-height: 118px;
            border-radius: 24px;
        }

        :deep(.ai-footer-links) {
            flex: 0 0 auto;
            padding: 0px 20px 0px;
            margin-top: -20px;
            position: relative;
            z-index: 11;
        }

        :deep(.ai-footer-links__copyright) { display: none; }
    }
}

.ai-main-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    position: relative;
    .ai-welcome-back {
        position: absolute;
        top: 34px;
        right: 5vw;
        padding: 0 0 0 22px;
        border: 0;
        color: #666;
        background: url('@/assets/images/ai/back.svg') left center / 16px auto no-repeat;
        cursor: pointer;
        transition: .3s;
        &:hover{
            font-weight: 700;
        }
    }
}

@media (max-width: 900px) {
    .ai-main.is-home { padding-top: 8vh; }

    .ai-main.is-chat {
        .ai-composer-shell {
            width: calc(100% - 24px);
            margin-top: 8px;
        }

        :deep(.ai-footer-links) { padding: 8px 10px 10px; }
    }
}

@media (max-width: 600px) {
    .ai-main.is-home { padding: 38px 15px 14px; }

    .ai-main.is-chat {
        .ai-composer-shell {
            width: calc(100% - 24px);
            margin-top: 8px;
        }

        :deep(.ai-footer-links) { padding: 8px 10px 10px; }
    }
}
</style>
